import React, { useState, useCallback } from 'react'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { useHistory, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { isEmpty, kebabCase } from 'lodash'
import { toast } from 'react-toastify'

import Modal from 'components/Modal'
import { DialogModal } from 'components/DialogModal'
import TextBox from 'components/TextBox'

import {
  getIsSalesEventTypeLoading,
  getSalesEventType,
} from 'store/SalesEventTypes/selectors'
import { getCurrentVenueUuid } from 'store/Venues/selector'
import {
  createSalesEventType,
  deleteSalesEventType,
  updateSalesEventType,
} from 'store/SalesEventTypes/actions'
import { ORG_NAME } from 'utils/env'

const PriceLevelsForm = () => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showValidationErrors, setShowValidationErrors] =
    useState(false)

  const history = useHistory()
  const dispatch = useDispatch()
  const { state: urlQueryParamsState } = useLocation()

  const salesEventUuid = urlQueryParamsState?.salesEventUuid

  const salesEventType = useSelector((state) =>
    getSalesEventType(state, salesEventUuid)
  )
  const venueUuid = useSelector((state) => getCurrentVenueUuid(state))
  const isLoading = useSelector((state) =>
    getIsSalesEventTypeLoading(state)
  )

  const initialValues = {
    salesEventType: salesEventType?.name ?? '',
  }

  const validationSchema = Yup.object().shape({
    salesEventType: Yup.string().required('Required'),
  })

  const modalBody = (
    <div className="dialog-modal__body-text">
      {`Are you sure you want to delete ${initialValues.salesEventType}
      This will delete all prices for this level and cannot be undone.`}
    </div>
  )
  const isNewPriceLevel = salesEventUuid === 'new'
  const modalTitle = isNewPriceLevel
    ? 'Add a Price Level'
    : 'Price Level'

  const disableUpdateButton = (dirty, errors) => {
    const disableButton =
      !dirty || (showValidationErrors && !isEmpty(errors))

    return disableButton
  }

  const onCloseModal = () =>
    history.push({
      state: { salesEventUuid: null },
    })

  const onSubmit = (values) => {
    if (isNewPriceLevel) {
      const newSalesEvent = {
        sales_event_types: [
          {
            name: values.salesEventType,
            venue_uuid: venueUuid,
            // this key has to be name not uuid, because name is uniqe
            external_ref_id: kebabCase(values.salesEventType),
            organization_name: ORG_NAME,
          },
        ],
      }
      dispatch(createSalesEventType(newSalesEvent)).then(
        (response) => {
          // if action payload is empty, then stay on the same page
          if (isEmpty(response?.payload)) return

          history.push({ state: { salesEventUuid: null } })
        }
      )
    } else {
      const editedSalesEvent = {
        sales_event_types: [
          { ...salesEventType, name: values.salesEventType },
        ],
      }
      dispatch(updateSalesEventType(editedSalesEvent)).then(
        (response) => {
          // if action payload is empty, then stay on the same page
          if (isEmpty(response?.payload)) return

          history.push({ state: { salesEventUuid: null } })
        }
      )
    }
  }

  const onDelete = useCallback(() => {
    dispatch(deleteSalesEventType(salesEventType.externalRefId)).then(
      (response) => {
        // if action payload is empty, then stay on the same page
        if (isEmpty(response?.payload)) return

        history.push({ state: { salesEventUuid: null } })
      }
    )

    // eslint-disable-next-line
  }, [dispatch, history])

  return (
    <Modal
      title={modalTitle}
      onClose={onCloseModal}
      className="-narrow"
      isLoading={isLoading}
      onRequestClose={onCloseModal}
    >
      <div className="modal-body">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({ dirty, errors }) => (
            <Form>
              <div className="item-settings">
                <Field
                  component={TextBox}
                  label="Name"
                  name="salesEventType"
                />
                {!isNewPriceLevel && (
                  <button
                    className="btn -block -outline -space-top"
                    onClick={setShowDeleteDialog}
                    type="button"
                  >
                    Delete Price Level
                  </button>
                )}
                <DialogModal
                  closeModalCallback={setShowDeleteDialog}
                  confirmationCallback={onDelete}
                  modalButtonText="Confirm"
                  modalIsOpen={showDeleteDialog}
                  modalTitle="Delete Price Level"
                  modalBody={modalBody}
                />
                <div className="btn-wrapper">
                  <button
                    className="btn -update"
                    onClick={() => setShowValidationErrors(true)}
                    disabled={disableUpdateButton(dirty, errors)}
                    type="submit"
                  >
                    Save
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}

PriceLevelsForm.propTypes = {}

export default PriceLevelsForm
